// features/projects/templates/continuum/resources.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM RESOURCES — Tab 4
//
// The management surface for everything the project pulls from:
//   - Ecological data sources (GBIF, IUCN, eBird, LPI, Earth Engine)
//   - Human system datasets (OWID, FDIC, UNESCO, ...)
//   - Files & Bibliography (TBD — Phase 2)
//
// The two upper sections read from /api/continuum/sources, which
// merges the static SOURCES registry with each workspace's
// continuum_data_sources row (status, record_count, last_sync,
// last_error). "Refresh now" hits /api/continuum/sync per source.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';

const STATUS_META = {
  live:    { color: C.sage,   label: 'Live' },
  partial: { color: C.gold,   label: 'Partial' },
  pending: { color: C.dim,    label: 'Pending' },
  error:   { color: C.terra,  label: 'Error' },
};

const AUTH_META = {
  public:          { label: 'Public · no auth' },
  api_key:         { label: 'API key' },
  api_token:       { label: 'API token' },
  oauth_research:  { label: 'OAuth · research account' },
  bulk_csv:        { label: 'Bulk CSV upload' },
};

export default function ContinuumResources({ workspaceId }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingKey, setSyncingKey] = useState(null);
  // Sync All state: which keys are currently syncing in the bulk run
  const [bulkSyncing, setBulkSyncing] = useState(null); // null | Set<sourceKey>

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/sources?workspace_id=${workspaceId}`);
    if (r.ok) {
      const j = await r.json();
      setSources(j.sources || []);
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function syncSource(sourceKey) {
    setSyncingKey(sourceKey);
    try {
      await fetch('/api/continuum/sync', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, source_key: sourceKey }),
      });
    } catch { /* swallow — error is reflected in source row */ }
    await reload();
    setSyncingKey(null);
  }

  // Sync All — fire all network-based sources in parallel. Skips
  // bulk_csv sources (LPI, Glottolog) since those need the loader scripts.
  async function syncAll() {
    const networkSources = sources.filter(s => s.authMode !== 'bulk_csv');
    if (networkSources.length === 0) return;
    const keys = new Set(networkSources.map(s => s.key));
    setBulkSyncing(keys);
    try {
      await Promise.allSettled(
        networkSources.map(s =>
          fetch('/api/continuum/sync', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ workspace_id: workspaceId, source_key: s.key }),
          }).catch(() => {/* swallow */})
        )
      );
    } finally {
      await reload();
      setBulkSyncing(null);
    }
  }

  const isAnySyncing = !!bulkSyncing || !!syncingKey;
  const networkSourceCount = sources.filter(s => s.authMode !== 'bulk_csv').length;

  const ecoSources   = sources.filter(s => s.category === 'ecological');
  const humanSources = sources.filter(s => s.category === 'human');

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.sans, fontSize: 13, fontWeight: 300, lineHeight: 1.6,
      overflow: 'auto', padding: '24px 32px',
    }}>
      <PageHeader onSyncAll={syncAll} bulkSyncing={bulkSyncing} networkSourceCount={networkSourceCount} disabled={isAnySyncing} />

      <SectionHeader
        title="Ecological Data Sources"
        subtitle={`Species, populations, ecosystems · ${ecoSources.filter(s => s.status === 'live').length} live, ${ecoSources.length} total`}
        accent={C.sage}
      />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:32 }}>
        {loading && <Skeleton />}
        {!loading && ecoSources.map(src => (
          <SourceCard
            key={src.key}
            src={src}
            syncing={syncingKey === src.key || (bulkSyncing && bulkSyncing.has(src.key))}
            onSync={() => syncSource(src.key)}
          />
        ))}
      </div>

      <SectionHeader
        title="Human System Datasets"
        subtitle={`Communities, languages, organizations, civilizations · ${humanSources.filter(s => s.status === 'live').length} live, ${humanSources.length} total`}
        accent={C.teal}
      />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:32 }}>
        {loading && <Skeleton />}
        {!loading && humanSources.map(src => (
          <SourceCard
            key={src.key}
            src={src}
            syncing={syncingKey === src.key || (bulkSyncing && bulkSyncing.has(src.key))}
            onSync={() => syncSource(src.key)}
          />
        ))}
      </div>

      <SectionHeader
        title="Files & Bibliography"
        subtitle="Papers, transcripts, datasets, citations"
        accent={C.amber}
      />
      <div style={{
        padding:'24px 28px',
        background: C.s1, border: `1px dashed ${C.b3}`, borderRadius: 8,
        color: C.dim, fontSize: 12, lineHeight: 1.7,
      }}>
        File library coming next. For now, papers + datasets live in
        the local research folder. Drop new materials there and they'll
        be picked up when this section ships.
      </div>
    </div>
  );
}

// ─── Page header ───────────────────────────────────────────
function PageHeader({ onSyncAll, bulkSyncing, networkSourceCount, disabled }) {
  const isBulk = !!bulkSyncing;
  return (
    <div style={{ marginBottom: 24, display:'flex', alignItems:'flex-end', gap: 24 }}>
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: F.mono, fontSize: 9, letterSpacing:'0.18em',
          textTransform:'uppercase', color: C.dim, marginBottom: 6,
        }}>
          The Continuum · Phase 1
        </div>
        <div style={{
          fontFamily: F.serif, fontSize: 26, fontWeight: 300,
          color: C.text, marginBottom: 4,
        }}>
          Resources
        </div>
        <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.7, maxWidth: 720 }}>
          Every dataset, API, and material the simulation pulls from.
          Click <em style={{ color: C.gold, fontStyle: 'normal' }}>Refresh</em> to re-sync any
          live source — or hit <em style={{ color: C.gold, fontStyle: 'normal' }}>Sync All</em> after
          adding new species to refresh everything in parallel. Sources
          awaiting credentials show their setup instructions inline.
        </div>
      </div>
      {networkSourceCount > 0 && (
        <button
          onClick={onSyncAll}
          disabled={disabled}
          title={`Refresh all ${networkSourceCount} network-based sources in parallel (skips bulk-CSV sources)`}
          style={{
            flexShrink: 0,
            background: isBulk ? `${C.teal}15` : `${C.gold}10`,
            border: `1px solid ${isBulk ? C.teal : C.gold}`,
            borderRadius: 6,
            color: isBulk ? C.teal : C.gold,
            fontFamily: F.mono,
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '10px 18px',
            cursor: disabled ? 'wait' : 'pointer',
            transition: 'all .15s',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => { if (!disabled && !isBulk) e.currentTarget.style.background = `${C.gold}20`; }}
          onMouseLeave={(e) => { if (!disabled && !isBulk) e.currentTarget.style.background = `${C.gold}10`; }}
        >
          {isBulk
            ? `⟳ Syncing ${bulkSyncing.size}…`
            : `↻ Sync All (${networkSourceCount})`}
        </button>
      )}
    </div>
  );
}

// ─── Section header ────────────────────────────────────────
function SectionHeader({ title, subtitle, accent }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display:'flex', alignItems:'center', gap:10, marginBottom:4,
      }}>
        <div style={{ width:6, height:6, borderRadius:'50%', background: accent }} />
        <div style={{
          fontFamily: F.serif, fontSize: 17, fontWeight: 400,
          color: C.text, letterSpacing:'0.01em',
        }}>{title}</div>
      </div>
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing:'0.1em',
        color: C.dim, marginLeft: 16,
      }}>{subtitle}</div>
    </div>
  );
}

// ─── Source card ───────────────────────────────────────────
function SourceCard({ src, syncing, onSync }) {
  const status = STATUS_META[src.status] || STATUS_META.pending;
  const auth   = AUTH_META[src.authMode] || { label: src.authMode || '—' };
  const accent = src.category === 'ecological' ? C.sage : C.teal;

  const lastSync = src.last_sync_at
    ? new Date(src.last_sync_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div style={{
      background: C.s1, border: `1px solid ${C.b2}`, borderRadius: 8,
      padding: '14px 16px', display:'flex', flexDirection:'column', gap:10,
    }}>
      {/* Top row */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
        <div style={{
          width:8, height:8, borderRadius:'50%', flexShrink:0, marginTop:6,
          background: status.color,
        }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{
            fontFamily: F.serif, fontSize: 15, fontWeight: 400,
            color: C.text, lineHeight: 1.3,
          }}>
            {src.name}
          </div>
          <div style={{
            fontFamily: F.mono, fontSize: 9, letterSpacing:'0.08em',
            color: C.dim, marginTop: 2,
          }}>
            {auth.label}
            {' · '}
            {src.dynamics?.join(' / ') || '—'}
          </div>
        </div>
        <div style={{
          fontFamily: F.mono, fontSize: 9, letterSpacing:'0.08em',
          padding:'2px 8px', borderRadius:3,
          background: `${status.color}15`, color: status.color,
          textTransform:'uppercase', flexShrink:0,
        }}>
          {status.label}
        </div>
      </div>

      {/* Description */}
      <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.6 }}>
        {src.description}
      </div>

      {/* Stats row */}
      <div style={{
        display:'flex', alignItems:'center', gap:14,
        padding:'8px 10px', background: C.s2, borderRadius: 5,
        border: `1px solid ${C.b1}`,
      }}>
        <Stat label="Records" value={src.record_count?.toLocaleString() || '0'} color={accent} />
        <div style={{ width:1, height:18, background: C.b2 }} />
        <Stat label="Last sync" value={lastSync || '—'} color={C.mid} mono />
      </div>

      {/* Errors */}
      {src.last_error && (
        <div style={{
          padding:'8px 10px',
          background: 'rgba(196,80,80,.06)',
          border: '1px solid rgba(196,80,80,.25)',
          borderRadius: 5, fontSize: 11, color: C.terra,
          fontFamily: F.mono, lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {summarizeError(src.last_error)}
        </div>
      )}

      {/* Setup help (only when not live yet) */}
      {src.status !== 'live' && src.setupHelp && (
        <div style={{
          padding:'8px 10px', background: 'rgba(200,169,110,.05)',
          border: '1px solid rgba(200,169,110,.18)', borderRadius: 5,
          fontSize: 11, color: C.gold, lineHeight: 1.55,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing:'0.14em',
            textTransform:'uppercase', color: C.gold,
            opacity: 0.85, marginBottom: 3,
          }}>
            Setup
          </div>
          {src.setupHelp}
        </div>
      )}

      {/* Actions */}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
        <button
          onClick={onSync}
          disabled={syncing}
          title={src.authMode === 'bulk_csv'
            ? 'Recount loaded rows (re-load via the script in Setup)'
            : 'Refresh from source'}
          style={{
            fontFamily: F.mono, fontSize: 10, letterSpacing:'0.06em',
            padding:'5px 14px', borderRadius: 4,
            cursor: syncing ? 'wait' : 'pointer',
            background: syncing ? `${C.teal}15` : `${accent}10`,
            border: `1px solid ${syncing ? C.teal : `${accent}50`}`,
            color: syncing ? C.teal : accent,
            transition: 'all .15s', textTransform:'uppercase',
          }}
        >
          {syncing ? '⟳ Syncing…' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, color, mono }) {
  return (
    <div style={{ display:'flex', flexDirection:'column' }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing:'0.12em',
        textTransform:'uppercase', color: C.dim, marginBottom: 2,
      }}>{label}</div>
      <div style={{
        fontFamily: mono ? F.mono : F.sans,
        fontSize: 13, color, fontWeight: 500,
      }}>{value}</div>
    </div>
  );
}

// ─── Skeleton (loading) ────────────────────────────────────
function Skeleton() {
  return (
    <>
      {[0, 1].map(i => (
        <div key={i} style={{
          background: C.s1, border: `1px solid ${C.b2}`, borderRadius: 8,
          padding: '14px 16px', height: 140,
          opacity: 0.4,
        }} />
      ))}
    </>
  );
}

// ─── Helpers ───────────────────────────────────────────────
function summarizeError(jsonStr) {
  try {
    const arr = JSON.parse(jsonStr);
    if (Array.isArray(arr) && arr.length) {
      const first = arr[0];
      const msg = first.error || JSON.stringify(first);
      return arr.length === 1
        ? msg
        : `${msg}  (+${arr.length - 1} more)`;
    }
  } catch { /* fall through */ }
  return jsonStr;
}
